import React from 'react'
import { Tooltip } from 'antd'
import styled from 'styled-components/macro'
import {
  formatSuccessHubBadgeDate,
  resolveSuccessHubReceiptWhy,
  successHubBadgeColourToken,
  successHubBadgeInitials,
  successHubBadgeMarkSize,
  successHubBadgeUsesDarkText,
} from 'src/utils/growthEngineSuccessHub'
import {
  SUCCESS_HUB_PROGRESS_TOKENS,
  successHubProgressRowBorder,
} from 'src/utils/successHubProgressTokens'

const T = SUCCESS_HUB_PROGRESS_TOKENS

/**
 * Coded letter/number mark + category colour. Receipt variant sits on a
 * pale nested row (same rhythm as collections / tiles) with outcome title,
 * earned date, and why — all from Hub config, so new achievements need no
 * artwork.
 *
 * Prestige (Black Belt) uses the same rounded mark shape as other badges —
 * dark plate, thin lime edge. Still a letter mark, not a medal ring or rarity.
 *
 * @param {'mark'|'receipt'} [variant]
 * @param {boolean} [prestige] — Black Belt / flagship receipt
 */
const SuccessHubBadge = ({
  badge,
  title,
  earnedAt,
  whyItMatters,
  variant = 'mark',
  prestige = false,
  size,
  className,
  'data-testid': dataTestId = 'success-hub-badge',
}) => {
  const colourToken = successHubBadgeColourToken(badge)
  const mark = successHubBadgeInitials(badge, title)
  const label = title || mark
  const dateLabel = formatSuccessHubBadgeDate(earnedAt)
  const why = resolveSuccessHubReceiptWhy({ whyItMatters, title })
  const markSize = successHubBadgeMarkSize({ variant, prestige, size })
  const isPrestige = Boolean(prestige)
  const tip =
    variant === 'receipt'
      ? [label, why].filter(Boolean).join(' — ')
      : why || label
  const markNode = (
    <Mark
      $token={colourToken}
      $size={markSize}
      $darkText={successHubBadgeUsesDarkText(colourToken)}
      $prestige={isPrestige}
      className={variant === 'receipt' ? undefined : className}
      data-testid={variant === 'receipt' ? `${dataTestId}-mark` : dataTestId}
      data-colour-token={colourToken}
      data-prestige={isPrestige ? 'true' : undefined}
      aria-hidden={variant === 'receipt' ? true : undefined}
      aria-label={variant === 'receipt' ? undefined : label}
    >
      {mark}
    </Mark>
  )

  if (variant !== 'receipt') {
    return tip ? (
      <Tooltip title={tip} mouseEnterDelay={0.25}>
        {markNode}
      </Tooltip>
    ) : (
      markNode
    )
  }

  return (
    <Tooltip title={tip || undefined} mouseEnterDelay={0.25}>
      <ReceiptCard
        className={className}
        data-testid={dataTestId}
        data-colour-token={colourToken}
        data-prestige={isPrestige ? 'true' : undefined}
        $token={colourToken}
        $prestige={isPrestige}
        aria-label={label}
      >
        {markNode}
        <Copy>
          {title ? (
            <Title data-testid={`${dataTestId}-title`}>{title}</Title>
          ) : null}
          {dateLabel ? (
            <DateLine data-testid={`${dataTestId}-date`}>{dateLabel}</DateLine>
          ) : null}
          {why ? <Why data-testid={`${dataTestId}-why`}>{why}</Why> : null}
        </Copy>
      </ReceiptCard>
    </Tooltip>
  )
}

/**
 * Mark fills — finance oliveGrey is darkened so IN/GR read as a family,
 * not page chrome. Prestige uses seaweed from config.
 */
const TOKEN_FALLBACKS = {
  seaweedGreen: '#132d07',
  lettuceGreen: '#b5d922',
  bbqBlack: '#000000',
  oliveGrey: '#5c5c5c',
  avocadoGreen: '#B4D820',
}

const markFill = (theme, token) =>
  theme?.colours?.[token] && token !== 'oliveGrey'
    ? theme.colours[token]
    : TOKEN_FALLBACKS[token] || theme?.colours?.oliveGrey || '#5c5c5c'

const Mark = styled.span`
  align-items: center;
  background: ${({ theme, $token }) => markFill(theme, $token)};
  border: ${({ $prestige, theme }) =>
    $prestige
      ? `1.5px solid ${theme?.colours?.lettuceGreen || '#b5d922'}`
      : '1.5px solid transparent'};
  border-radius: ${T.radius.mark};
  box-sizing: border-box;
  color: ${({ theme, $darkText }) =>
    $darkText
      ? theme?.colours?.bbqBlack || '#000'
      : theme?.colours?.mayoWhite || '#fff'};
  cursor: help;
  display: inline-flex;
  flex-shrink: 0;
  font-size: ${({ $size }) => Math.max(12, Math.round($size * 0.42))}px;
  font-weight: 700;
  height: ${({ $size }) => $size}px;
  justify-content: center;
  letter-spacing: 0.04em;
  line-height: 1;
  text-transform: uppercase;
  width: ${({ $size }) => $size}px;
`

const ReceiptCard = styled.article`
  align-items: flex-start;
  background: ${({ theme }) => theme?.colours?.paleGrey || '#f7f7f7'};
  border: ${successHubProgressRowBorder()};
  border-left: 3px solid
    ${({ theme, $token, $prestige }) =>
      $prestige
        ? theme?.colours?.lettuceGreen || '#b5d922'
        : markFill(theme, $token)};
  border-radius: ${T.radius.row};
  box-sizing: border-box;
  cursor: help;
  display: flex;
  gap: ${T.space.sm};
  height: 100%;
  min-width: 0;
  padding: ${T.pad.row};
`

const Copy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const Title = styled.p`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: ${T.type.rowTitle};
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
`

const DateLine = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#919191'};
  font-size: ${T.type.meta};
  line-height: 1.4;
  margin: 0;
`

const Why = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#919191'};
  font-size: ${T.type.rowMeta};
  line-height: 1.45;
  margin: 0;
`

export default SuccessHubBadge
